"use client";

import React, { useState } from "react";
import { EmblaCarouselType } from "embla-carousel/components/EmblaCarousel";
import { ChevronRight } from "src/app/components/Icon";

export interface PaginationProps {
  scrollSnaps: number[];
  selectedIndex: number;
  onDotButtonClick?: (index: number) => void;
  emblaApi: EmblaCarouselType | undefined;
  variant?: boolean;
  maxVisibleDots?: number;
}

// Utility to calculate visible dots
const getVisibleDots = (
  totalDots: number,
  selectedIndex: number,
  maxVisibleDots: number
) => {
  if (totalDots <= maxVisibleDots) {
    return Array.from({ length: totalDots }, (_, i) => i);
  }

  const half = Math.floor(maxVisibleDots / 2);
  let start = Math.max(0, selectedIndex - half);
  let end = start + maxVisibleDots;

  if (end > totalDots) {
    end = totalDots;
    start = end - maxVisibleDots;
  }

  return Array.from({ length: end - start }, (_, i) => start + i);
};

const Pagination: React.FC<PaginationProps> = ({
  scrollSnaps,
  selectedIndex,
  onDotButtonClick,
  emblaApi,
  variant,
  maxVisibleDots = 7,
}) => {
  if (scrollSnaps.length < 2) {
    return null;
  }

  const visibleDots = getVisibleDots(
    scrollSnaps.length,
    selectedIndex,
    maxVisibleDots
  );

  return (
    <div className="itens mt-10 flex gap-10 w-max mx-auto items-center">
      {emblaApi?.canScrollPrev && (
        <div
          onClick={() => emblaApi && emblaApi.scrollPrev()}
          className={`next hidden prev transform rotate-[180deg] ${
            variant && "!block"
          }`}
        >
          <PaginationIcon />
        </div>
      )}

      <div className="pagination flex items-center gap-2 xlg:gap-1 transition-all">
        {visibleDots.map((index) => {
          const isActive = index === selectedIndex;
          const isNeighbor = Math.abs(index - selectedIndex) === 1;
          return (
            <div
              className="cursor-pointer"
              key={index}
              onClick={() => {
                if (onDotButtonClick) {
                  onDotButtonClick(index);
                }
              }}
            >
              {isActive ? <Active /> : <Inactive isNeighbor={isNeighbor} />}
            </div>
          );
        })}
      </div>

      {emblaApi?.canScrollNext && (
        <div
          onClick={() => emblaApi && emblaApi.scrollNext()}
          className={`next hidden ${variant && "!block"}`}
        >
          <PaginationIcon />
        </div>
      )}
    </div>
  );
};

export default Pagination;

export const NavigateIcon = () => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      <svg
        width="148"
        height="148"
        className="transition-all duration-500"
        viewBox="0 0 148 148"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g
          className="transition-all duration-500"
          filter="url(#filter0_d_1269_174128)"
        >
          <rect
            className="transition-all duration-500"
            x="50"
            y="46"
            width="48"
            height="48"
            rx="8"
            fill="var(--primary-color)"
            fillOpacity={hovered ? "1" : "0.1"}
            shapeRendering="crispEdges"
          />
          <path
            d="M68.3359 60.0235C68.2278 59.909 68.1432 59.7743 68.087 59.6271C68.0309 59.4799 68.0043 59.3231 68.0087 59.1657C68.0132 59.0082 68.0486 58.8532 68.113 58.7094C68.1774 58.5656 68.2694 58.4359 68.3839 58.3277C68.4984 58.2195 68.6331 58.1349 68.7803 58.0788C68.9275 58.0226 68.9843 57.996 69.2418 58.0005C69.3992 58.0049 69.5543 58.0404 69.6981 58.1047C69.8418 58.1691 69.9715 58.2612 70.0797 58.3757L80.2737 69.1693C80.4843 69.392 80.6016 69.6868 80.6016 69.9933C80.6016 70.2997 80.4843 70.5945 80.2737 70.8172L70.0797 81.612C69.9722 81.729 69.8426 81.8235 69.6983 81.89C69.554 81.9565 69.3979 81.9936 69.2391 81.9992C69.0803 82.0049 68.922 81.9789 68.7733 81.9229C68.6247 81.8668 68.4886 81.7818 68.3731 81.6727C68.2576 81.5636 68.1649 81.4326 68.1004 81.2874C68.036 81.1422 68.001 80.9857 67.9975 80.8268C67.9941 80.668 68.0222 80.510 68.0803 80.3621C68.1384 80.2143 68.2253 80.0794 68.3359 79.9654L77.7528 69.9933L68.3359 60.0235Z"
            fill={hovered ? "white" : "var(--primary-color)"}
          />
        </g>
        <defs>
          <filter
            id="filter0_d_1269_174128"
            x="0"
            y="0"
            width="148"
            height="148"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="25" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.129412 0 0 0 0 0.129412 0 0 0 0 0.129412 0 0 0 0.05 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174128"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174128"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    </div>
  );
};

const Active = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="transition-transform duration-300 scale-125"
    >
      <circle
        opacity="0.8"
        cx="10"
        cy="10"
        r="10"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

const Inactive = ({ isNeighbor = false }: { isNeighbor?: boolean }) => {
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`transition-transform duration-300 ${
        isNeighbor ? "scale-110" : "scale-100"
      }`}
    >
      <circle
        opacity="0.8"
        cx="4"
        cy="4"
        r="4"
        fill="var(--primary-color)"
        fillOpacity="0.1"
      />
    </svg>
  );
};

export const PaginationIcon = () => {
  return (
    <button className="bg-[rgba(var(--primary-color-rgb),0.1)] transition-all duration-500 grid place-items-center p-3 px-5 rounded-md hover:bg-themeColor hover:text-white text-themeColor">
      <ChevronRight />
    </button>
  );
};
